<template>
  <div class="astra-detail" v-loading="loading">
    <div v-if="!isMobile" class="detail-container">
      <el-breadcrumb v-if="!isMobile" separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/robot' }">{{ exchangeName }}</el-breadcrumb-item>
        <el-breadcrumb-item>
          {{ $t('robot.strategy_details') }}
          <span class="strategy-number">{{ strategyNumber }}</span>
        </el-breadcrumb-item>
      </el-breadcrumb>

      <el-card class="header-card" shadow="hover">
        <div class="strategy-header">
          <div class="strategy-identity">
            <img class="strategy-icon" src="../../assets/img/home/ai_vip_strategy_icon.png" alt="UPlus-Astra AI">
            <div>
              <div class="strategy-name-row">
                <span class="strategy-name">UPlus-Astra AI</span>
                 <span>{{ $t('robot.auto') }}：{{ $t('robot.grid_contract_strategy') }}</span>
                <img v-if="strategyRunning" class="running-icon" src="../../assets/img/robot/running.gif" alt="">
              
              </div>
              
              <div class="strategy-meta">
                <span>{{ $t('robot.create_time') }}：{{ createTime }}</span>
              </div>
            </div>
          </div>

          <div v-if="switchValue != '2'" class="strategy-actions">
            <el-button v-if="strategyRunning" class="outline-button" @click="pauseStrategy">
              {{ $t('robot.pause_strategy') }}
            </el-button>
            <el-button v-else class="primary-button" @click="startStrategy">
              {{ $t('robot.open_strategy') }}
            </el-button>
            <el-button class="outline-button" @click="deleteStrategy">
              {{ $t('robot.del_strategy') }}
            </el-button>
          </div>
        </div>
      </el-card>

      <el-card class="summary-card" shadow="hover">
        <div class="metric-grid">
          <div class="metric-item">
            <span class="metric-label">{{ $t('robot.Total_revenue') }}</span>
            <span class="metric-value" :class="numberClass(totalProfit)">
              {{ formatNumber(totalProfit) }} {{ settlementCoin }}
            </span>
          </div>
          <div class="metric-item metric-center">
            <span class="metric-label">{{ $t('robot.billing_quantity') }}</span>
            <span class="metric-value">{{ formatNumber(orderCount, 0) }} {{ $t('robot.order') }}</span>
          </div>
          <div class="metric-item metric-right">
            <span class="metric-label">{{ $t('robot.yield') }} | {{ $t('robot.total_annualization') }}</span>
            <span class="metric-value" :class="numberClass(profitRate)">
              {{ formatNumber(profitRate) }}% | {{ formatNumber(annualizedRate) }}%
            </span>
          </div>
        </div>

        <div class="section-divider"></div>

        <div class="metric-grid metric-grid-secondary">
          <div class="metric-item">
            <span class="metric-label">{{ $t('robot.investment_amount') }}</span>
            <span class="secondary-value">{{ formatNumber(investmentAmount) }} {{ settlementCoin }}</span>
          </div>
          <div class="metric-item metric-center">
            <span class="metric-label">{{ $t('robot.exchange_assets') }}</span>
            <span class="secondary-value">{{ formatNumber(exchangeBalance) }} {{ settlementCoin }}</span>
          </div>
          <div class="metric-item metric-right">
            <span class="metric-label">{{ $t('robot.robot_run_state') }}</span>
            <span class="secondary-value" :class="strategyRunning ? 'positive' : ''">{{ strategyStateText }}</span>
          </div>
        </div>
      </el-card>

      <el-card class="info-card" shadow="hover">
        <div class="card-heading">{{ $t('robot.strategy_details') }}</div>
        <div class="section-divider compact"></div>
        <div class="info-grid">
          <div class="info-item">
            <span>{{ $t('robot.investment_amount') }}</span>
            <strong>{{ formatNumber(investmentAmount) }} {{ settlementCoin }}</strong>
          </div>
          <div class="info-item">
            <span>{{ $t('robot.leverage') }}</span>
            <strong>{{ leverage }}x</strong>
          </div>
          <div class="info-item">
            <span>{{ $t('robot.trading_place') }}</span>
            <strong>{{ exchangeName }}</strong>
          </div>
          <div class="info-item">
            <span>{{ $t('robot.create_time') }}</span>
            <strong>{{ createTime }}</strong>
          </div>
        </div>
      </el-card>

      <el-card class="chart-card" shadow="hover" v-loading="profitLoading">
        <div class="card-heading chart-heading">
          <span>{{ $t('robot.earnings_change') }}</span>
          <el-date-picker
            v-model="profitMonth"
            class="profit-month-picker"
            size="mini"
            type="month"
            format="yyyy-MM"
            value-format="yyyy-MM"
            :editable="false"
            :clearable="false"
            :placeholder="$t('user_center.select_month')"
            @change="handleProfitMonthChange">
          </el-date-picker>
        </div>
        <div class="section-divider compact"></div>
        <div ref="profitChart" class="profit-chart" v-show="profitPoints.length"></div>
        <el-empty v-if="!profitPoints.length" :description="$t('robot.no_data')"></el-empty>
      </el-card>

      <el-card class="records-card" shadow="hover">
        <el-tabs v-model="activeTab" @tab-click="handleTabChange">
          <el-tab-pane :label="tabLabel('robot.positions', positionTotal)" name="positions"></el-tab-pane>
          <el-tab-pane :label="tabLabel('robot.current_delegation', orderTotal)" name="orders"></el-tab-pane>
        </el-tabs>

        <div class="table-scroll" v-if="activeTab === 'positions'">
          <el-table :data="positionList" style="width: 100%" :empty-text="$t('robot.no_data')">
            <el-table-column min-width="150" align="center" :label="$t('robot.transaction_pair')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['symbol', 'instrument_id', 'instId'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="100" align="center" :label="$t('robot.business_line')">
              <template>{{ $t('robot.perpetual') }}</template>
            </el-table-column>
            <el-table-column min-width="130" align="center" :label="$t('robot.position_type')">
              <template slot-scope="scope">
                <span>{{ $t('robot.cross') }}</span>
                <span :class="positionSideClass(scope.row)" class="side-text">
                  {{ positionSideText(scope.row) }} {{ rowValue(scope.row, ['leverage', 'lever'], leverage) }}x
                </span>
              </template>
            </el-table-column>
            <el-table-column min-width="110" align="center" :label="$t('robot.total_position')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['total_size', 'size', 'position', 'amount'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="120" align="center" :label="$t('robot.avg_price')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['avg_price', 'average_price', 'entry_price'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="120" align="center" :label="$t('robot.mark_price')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['mark_price', 'market_price', 'current_price'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="110" align="center" :label="$t('robot.pl')">
              <template slot-scope="scope">
                <span :class="numberClass(rowValue(scope.row, ['profit', 'unrealized_profit', 'profit_loss'], 0))">
                  {{ formatNumber(rowValue(scope.row, ['profit', 'unrealized_profit', 'profit_loss'], 0)) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column min-width="100" align="center" :label="$t('robot.yield')">
              <template slot-scope="scope">
                <span :class="numberClass(rowValue(scope.row, ['profit_rate', 'yield_rate', 'rate'], 0))">
                  {{ formatNumber(rowValue(scope.row, ['profit_rate', 'yield_rate', 'rate'], 0)) }}%
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="table-scroll" v-else>
          <el-table :data="orderList" style="width: 100%" :empty-text="$t('robot.no_data')">
            <el-table-column min-width="160" align="center" :label="$t('robot.entrusted_time')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['create_time', 'created_at', 'time'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="150" align="center" :label="$t('robot.transaction_pair')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['symbol', 'instrument_id', 'instId'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="120" align="center" :label="$t('robot.side')">
              <template slot-scope="scope">
                <span :class="orderSideClass(scope.row)">{{ orderSideText(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column min-width="110" align="center" :label="$t('robot.entrusted_quantity')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['size', 'quantity', 'amount', 'order_size'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="110" align="center" :label="$t('robot.entrusted_price')">
              <template slot-scope="scope">{{ rowValue(scope.row, ['price', 'order_price'], '--') }}</template>
            </el-table-column>
            <el-table-column min-width="110" align="center" :label="$t('robot.state')">
              <template slot-scope="scope">{{ orderStateText(scope.row) }}</template>
            </el-table-column>
          </el-table>
          <el-pagination
              v-if="orderTotal > pageSize"
              class="records-pagination"
              layout="prev, pager, next"
              :page-size="pageSize"
              :current-page="pageNum"
              :total="orderTotal"
              @current-change="handlePageChange">
          </el-pagination>
        </div>
      </el-card>
    </div>

    <div v-else style="">
      <div style="height: 50px;background-color: white;position:relative;">
        <i class="el-icon-arrow-left" style="display:inline-block;font-size: 20px;line-height: 50px;margin-left: 20px" @click="$router.back()"></i>
        <span style="display:inline-block;position: absolute; margin: auto; left: 50%; top: 50%;transform: translate(-50%, -50%);">{{ $t('robot.strategy_details') }}<span style="display: inline-block;margin-left: 5px">{{ strategyNumber || '(NO.)' }}</span></span>
        <img v-if="robotType === 0" style="position: absolute;right: 0;top: 0;width: 20px;height: 22px" src="../../assets/img/robot/binance_sign.svg">
         
        <img v-else style="position: absolute;right: 0;top: 0;width: 20px;height: 22px" src="../../assets/img/robot/okex_sign.svg">
      </div>

      <el-card style="" shadow="hover">
        <div class="detail_title" style="border-bottom: unset">
          <span style="font-size: 16px;margin-right: 9px;">UPlus-Astra AI</span>
          <span v-if="robotType == 1" style="padding: 4px 10px;font-size: 14px;margin-left: 0px;width: fit-content">{{ $t('robot.auto') }}：{{ $t('robot.grid_contract_strategy') }}</span>
          <img v-if="strategyRunning" style="vertical-align: middle;height: unset; width: 20px;margin-left:10px" src="../../assets/img/robot/running.gif">
          
          <span style="display: block;font-size: 12px;margin-left: 0;margin-top: 5px">{{ $t('robot.create_time') }} {{ createTime }}</span>
          <div v-if="switchValue != '2'" style="display: inline-block;position: absolute;right: 0;top: 0">
            <el-button v-if="strategyRunning" class="detail_title_button_w" style="display: block;margin: 3px;padding: 4px 15px;" @click="pauseStrategy">{{ $t('robot.pause') }}</el-button>
            <el-button v-else class="detail_title_button_w" style="display: block;margin: 3px;padding: 4px 15px;" @click="startStrategy">{{ $t('robot.start') }}</el-button>
            <el-button class="detail_title_button_w" style="display: block;margin: 3px;padding: 4px 15px;" @click="deleteStrategy">{{ $t('robot.del') }}</el-button>
          </div>
        </div>
      </el-card>

      <el-card style="margin-top: 12px;min-height: 500px" shadow="hover">
        <el-tabs v-model="mobileSection" @tab-click="handleMobileSectionChange">
          <el-tab-pane :label="$t('robot.revenue_details')" name="1"></el-tab-pane>
          <el-tab-pane :label="$t('robot.policy_information')" name="2"></el-tab-pane>
          <el-tab-pane :label="$t('robot.policy_delegation')" name="3"></el-tab-pane>
        </el-tabs>

        <div v-if="mobileSection === '1'" style="position: relative">
          <span style="font-size: 14px;padding-bottom: 1px;border-bottom: #b4bccc 1px dashed">{{ $t('robot.Total_revenue') }}(<span style="color: #72777E">{{ settlementCoin }}</span>)</span>
          <span :class="numberClass(totalProfit)" style="display:block;margin-top:6px;font-size: 22px;font-weight: 600">{{ formatNumber(totalProfit) }}</span>
          <div style="position: absolute;top: 0;right: 0;text-align: right">
            <span style="display:inline-block;font-size: 14px;padding-bottom: 1px;border-bottom: #b4bccc 1px dashed;width: fit-content">{{ $t('robot.yield') }} | {{ $t('robot.total_annualization') }}</span>
            <span :class="numberClass(profitRate)" style="display:block;font-size: 19px;margin-top:6px;font-weight: 600">{{ formatNumber(profitRate) }}% | {{ formatNumber(annualizedRate) }}%</span>
          </div>
          <div style="width: 100%;height: 1px;background: #EAECEF;margin: 15px 3% 15px 0"></div>
          <div style="font-size: 14px;position:relative;margin-top: 10px;color: #72777E"><span>{{ $t('robot.billing_quantity') }}</span><span style="position:absolute;right:0;color:#1E2329">{{ formatNumber(orderCount, 0) }} {{ $t('robot.order') }}</span></div>
          <div style="font-size: 14px;position:relative;margin-top: 10px;color: #72777E"><span>{{ $t('robot.investment_amount') }}</span><span style="position:absolute;right:0;color:#1E2329">{{ formatNumber(investmentAmount) }} {{ settlementCoin }}</span></div>
          <div style="font-size: 14px;position:relative;margin-top: 10px;color: #72777E"><span>{{ $t('robot.exchange_assets') }}</span><span style="position:absolute;right:0;color:#1E2329">{{ formatNumber(exchangeBalance) }} {{ settlementCoin }}</span></div>
          <div style="font-size: 14px;position:relative;margin-top: 10px;color: #72777E"><span>{{ $t('robot.robot_run_state') }}</span><span :class="strategyRunning ? 'side_color_green' : ''" style="position:absolute;right:0;color:#1E2329">{{ strategyStateText }}</span></div>
          <div class="mobile-profit-change" v-loading="profitLoading">
            <div class="card-heading chart-heading">
              <span>{{ $t('robot.earnings_change') }}</span>
              <el-date-picker
                v-model="profitMonth"
                class="profit-month-picker"
                size="mini"
                type="month"
                format="yyyy-MM"
                value-format="yyyy-MM"
                :editable="false"
                :clearable="false"
                :placeholder="$t('user_center.select_month')"
                @change="handleProfitMonthChange">
              </el-date-picker>
            </div>
            <div class="section-divider compact"></div>
            <div ref="profitChart" class="profit-chart" v-show="profitPoints.length"></div>
            <el-empty v-if="!profitPoints.length" :description="$t('robot.no_data')"></el-empty>
          </div>
        </div>

        <div v-else-if="mobileSection === '2'" style="font-size: 14px;background-color: #FEFBF3">
          <div style="display:inline-block;width: 100%;position:relative;background-color: #FEFBF3;border-radius: 2px;vertical-align: top;padding: 17px 0">
            <span style="margin-left:10px;color:#72777E">{{ $t('robot.investment_amount') }}</span>
            <div style="display:inline-block;position:absolute;right:10px"><span class="total_revenue_content_bottom">{{ formatNumber(investmentAmount) }} {{ settlementCoin }}</span><span style="margin-left: 8px">{{ leverage }}X</span></div>
          </div>
          <div style="display:block;width: 100%;position:relative;background-color: #FEFBF3;border-radius: 2px;padding: 17px 0;vertical-align: top">
            <span style="margin-left:10px;color:#72777E">{{ $t('robot.trading_place') }}</span>
            <span style="float:right;margin-right:10px;color:#1E2329;font-weight:500">{{ exchangeName }}</span>
          </div>
          <div style="display:block;width: 100%;position:relative;background-color: #FEFBF3;border-radius: 2px;padding: 17px 0;vertical-align: top">
            <span style="margin-left:10px;color:#72777E">{{ $t('robot.create_time') }}</span>
            <span style="float:right;margin-right:10px;color:#1E2329;font-weight:500">{{ createTime }}</span>
          </div>
        </div>

        <div v-else style="width:100%">
          <div class="everyday-top">
            <el-tabs v-model="activeTab" @tab-click="handleTabChange">
              <el-tab-pane :label="tabLabel('robot.positions', positionTotal)" name="positions"></el-tab-pane>
              <el-tab-pane :label="tabLabel('robot.current_delegation', orderTotal)" name="orders"></el-tab-pane>
            </el-tabs>
          </div>
          <div class="robot-earn-mb" style="width:100%;min-width:281px">
            <table v-if="isMobile" class="table" style="width:100%">
              <tbody v-if="activeTab === 'positions'">
                <tr v-for="(row, index) in positionList" :key="row.id || row.position_id || index">
                  <td style="text-align:unset;width:100%;font-size:16px;border-bottom:#EAECEF 1px solid;padding:6px 0">
                    <div style="width:100%;height:26px;line-height:26px">
                      <span style="color:#000613;font-size:13px;font-weight:550">{{ rowValue(row, ['symbol', 'instrument_id', 'instId'], '--') }}</span>
                      <span style="font-size:12px;color:#E3B936;margin-left:10px;background:#FEFBF3;padding:2px 5px;border-radius:2px">{{ $t('robot.perpetual') }}</span>
                      <span style="font-size:12px;color:#72777E;margin-left:10px">{{ $t('robot.cross') }}</span>
                      <span :class="positionSideClass(row) === 'positive' ? 'side_color_green' : 'side_color_red'" style="margin-left:5px;font-size:12px">{{ positionSideText(row) }} {{ rowValue(row, ['leverage', 'lever'], leverage) }}x</span>
                    </div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.total_position') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['total_size', 'size', 'position', 'amount'], '--') }}</span></div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.avg_price') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['avg_price', 'average_price', 'entry_price'], '--') }}</span></div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.mark_price') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['mark_price', 'market_price', 'current_price'], '--') }}</span></div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.pl') }}({{ $t('robot.yield') }})</span><span :class="numberClass(rowValue(row, ['profit', 'unrealized_profit', 'profit_loss'], 0))" style="float:right;color:#000613">{{ formatNumber(rowValue(row, ['profit', 'unrealized_profit', 'profit_loss'], 0)) }} ({{ formatNumber(rowValue(row, ['profit_rate', 'yield_rate', 'rate'], 0)) }}%)</span></div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr v-for="(row, index) in orderList" :key="row.id || row.order_id || index">
                  <td style="text-align:unset;width:100%;font-size:16px;border-bottom:#EAECEF 1px solid;padding:6px 0">
                    <div style="width:100%;height:26px;line-height:26px">
                      <span style="color:#000613;font-size:13px;font-weight:550">{{ rowValue(row, ['symbol', 'instrument_id', 'instId'], '--') }}</span>
                      <span :class="orderSideClass(row) === 'positive' ? 'side_color_green' : 'side_color_red'" style="margin-left:10px;font-size:13px">{{ orderSideText(row) }}</span>
                      <span style="float:right;color:#72777E;font-size:12px">{{ orderStateText(row) }}</span>
                    </div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.entrusted_quantity') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['size', 'quantity', 'amount', 'order_size'], '--') }}</span></div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.entrusted_price') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['price', 'order_price'], '--') }}</span></div>
                    <div style="width:100%;height:30px;line-height:30px;font-size:12px"><span style="color:#72777E">{{ $t('robot.entrusted_time') }}</span><span style="float:right;color:#000613">{{ rowValue(row, ['create_time', 'created_at', 'time'], '--') }}</span></div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="isMobile" style="height:22px;margin-top:10px">
              <span style="font-size:14px;color:#000613;line-height:22px">{{ $t('robot.total') }} <span>{{ activeTab === 'positions' ? positionTotal : orderTotal }}</span> {{ $t('robot.strip') }}</span>
              <el-pagination v-if="activeTab === 'orders'" style="float:right;padding-right:0" small layout="prev, pager, next" :page-size="pageSize" :current-page="pageNum" :hide-on-single-page="orderTotal <= pageSize" :total="orderTotal" @current-change="handlePageChange"></el-pagination>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import {
  deleteNewStrategy,
  getNewStrategyDetail,
  getNewStrategyOrder,
  getNewStrategyPosition,
  getNewStrategyProfit,
  startOrPauseNewStrategy
} from '../../server/robot'

export default {
  name: 'RobotAiVipDetail',
  data() {
    return {
      loading: false,
      isMobile: false,
      items: {},
      dataItem: {},
      robotType: 1,
      robotState: '',
      switchValue: '1',
      mobileSection: '1',
      activeTab: 'positions',
      positionList: [],
      positionTotal: 0,
      orderList: [],
      orderTotal: 0,
      pageNum: 1,
      pageSize: 10,
      profitMonth: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      profitPoints: [],
      profitLoading: false,
      profitChart: null
    }
  },
  computed: {
    strategyNumber() {
      const rank = this.rowValue(this.items, ['rank'], '')
      if (rank === '') return ''
      return `(NO.${Number(rank) < 10 ? `0${rank}` : rank})`
    },
    exchangeName() {
      return this.robotType === 0 ? 'Binance' : 'OKX'
    },
    strategyRunning() {
      return String(this.rowValue(this.dataItem, ['state'], this.rowValue(this.items, ['state'], '2'))) === '1'
    },
    strategyStateText() {
      return this.strategyRunning ? this.$t('robot.robot_state_name1') : this.$t('robot.paused')
    },
    createTime() {
      return this.rowValue(this.dataItem, ['add_time', 'create_time', 'created_at'], this.rowValue(this.items, ['add_time', 'create_time'], '--'))
    },
    settlementCoin() {
      return this.rowValue(this.dataItem, ['coin', 'quote_coin', 'settle_coin'], this.rowValue(this.items, ['coin'], 'USDT')) || 'USDT'
    },
    totalProfit() {
      return this.rowValue(this.dataItem, ['profit_withdraw', 'profit_total', 'total_profit', 'profit'], this.rowValue(this.items, ['profit_total'], 0))
    },
    orderCount() {
      return this.rowValue(this.dataItem, ['profit_times', 'order_count', 'trade_count'], this.orderTotal)
    },
    profitRate() {
      return this.rowValue(this.dataItem, ['profit_rate', 'yield_rate', 'rate'], this.rowValue(this.items, ['profit_rate'], 0))
    },
    annualizedRate() {
      return this.rowValue(this.dataItem, ['profit_rate_year', 'annualized_rate', 'annualized_return'], 0)
    },
    investmentAmount() {
      return this.rowValue(this.dataItem, ['space', 'totalSpace', 'investment_amount'], this.rowValue(this.items, ['space', 'totalSpace'], 0))
    },
    exchangeBalance() {
      return this.rowValue(this.dataItem, ['exchange_balance', 'balance', 'available_balance'], 0)
    },
    leverage() {
      return this.rowValue(this.dataItem, ['leverage', 'lever'], 10)
    }
  },
  mounted() {
    try {
      this.items = sessionStorage.getItem('detailItem') ? JSON.parse(sessionStorage.getItem('detailItem')) : {}
    } catch (error) {
      this.items = {}
      sessionStorage.removeItem('detailItem')
    }
    if (this.$route.query.sid) this.$set(this.items, 'sid', this.$route.query.sid)
    if (this.$route.query.rank) this.$set(this.items, 'rank', this.$route.query.rank)
    this.switchValue = String(this.$route.query.switchValue || '1')
    this.robotState = this.$route.query.robotState || this.rowValue(this.items, ['robotState', 'robot_state'], '')
    this.robotType = this.resolveRobotType()
    this.updateResponsiveState()
    window.addEventListener('resize', this.updateResponsiveState)
    this.loadPage()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateResponsiveState)
    if (this.profitChart) {
      this.profitChart.dispose()
      this.profitChart = null
    }
  },
  methods: {
    updateResponsiveState() {
      this.isMobile = Boolean(this.$util._isMobile()) || window.innerWidth <= 900
      this.$nextTick(() => {
        if (this.profitPoints.length) {
          this.renderProfitChart()
        } else {
          this.resizeChart()
        }
      })
    },
    async loadPage() {
      if (!this.items.sid) {
        this.$message.warning(this.$t('robot.no_data'))
        this.$router.replace({ name: 'robot' })
        return
      }
      this.loading = true
      try {
        await Promise.all([
          this.fetchDetail(),
          this.fetchProfitChart(),
          this.fetchPositions(),
          this.fetchOrders()
        ])
      } finally {
        this.loading = false
      }
    },
    resolveRobotType() {
      const routeType = this.$route.query.robotType
      const itemType = this.rowValue(this.items, ['robotType', 'robot_type'], 1)
      const value = routeType !== undefined ? routeType : itemType
      return value === 0 || value === '0' || value === 'bn' ? 0 : 1
    },
    async fetchDetail() {
      const res = await getNewStrategyDetail(this.robotType, this.items.sid)
      if (!res) return
      if (res.success && res.data) {
        this.dataItem = Object.assign({}, this.items, res.data)
      } else if (!res.success) {
        this.$message.error(res.message)
      }
    },
    async fetchProfitChart() {
      const requestMonth = this.profitMonth
      this.profitLoading = true
      try {
        const res = await getNewStrategyProfit(this.robotType, requestMonth, this.items.sid)
        if (!res || requestMonth !== this.profitMonth) return
        if (res.success) {
          this.profitPoints = this.normalizeProfitPoints(res.data)
          this.$nextTick(this.renderProfitChart)
        } else {
          this.$message.error(res.message)
        }
      } finally {
        if (requestMonth === this.profitMonth) this.profitLoading = false
      }
    },
    handleProfitMonthChange(month) {
      if (!month) return
      this.profitPoints = []
      if (this.profitChart) this.profitChart.clear()
      this.fetchProfitChart()
    },
    async fetchPositions() {
      const res = await getNewStrategyPosition(this.robotType, this.items.sid, 0)
      if (!res) return
      if (res.success) {
        this.positionList = this.extractList(res.data)
        this.positionTotal = this.extractTotal(res.data, this.positionList.length)
      } else {
        this.$message.error(res.message)
      }
    },
    async fetchOrders() {
      const res = await getNewStrategyOrder(this.robotType, this.items.sid, 0, this.pageNum, this.pageSize)
      if (!res) return
      if (res.success) {
        this.orderList = this.extractList(res.data)
        this.orderTotal = this.extractTotal(res.data, this.orderList.length)
      } else {
        this.$message.error(res.message)
      }
    },
    handleTabChange() {
      if (this.activeTab === 'positions') {
        this.fetchPositions()
      } else {
        this.fetchOrders()
      }
    },
    handleMobileSectionChange() {
      if (this.mobileSection === '1') {
        this.$nextTick(this.renderProfitChart)
      } else if (this.mobileSection === '3') {
        this.handleTabChange()
      }
    },
    handlePageChange(page) {
      this.pageNum = page
      this.fetchOrders()
    },
    async startStrategy() {
      await this.updateStrategyState(1)
    },
    pauseStrategy() {
      this.$confirm(this.$t('robot.astra_pause_confirm'), '', {
        confirmButtonText: this.$t('robot.sure'),
        cancelButtonText: this.$t('robot.cancel'),
        type: 'warning'
      }).then(() => this.updateStrategyState(2)).catch(() => {})
    },
    async updateStrategyState(state) {
      const res = await startOrPauseNewStrategy(this.robotType, this.items.sid, state)
      if (!res) return
      if (res.success) {
        this.$set(this.items, 'state', state === 1 ? 1 : 2)
        this.$set(this.dataItem, 'state', state === 1 ? 1 : 2)
        this.$message.success(this.$t('robot.operation_success'))
        await this.fetchDetail()
      } else {
        this.$message.error(res.message)
      }
    },
    deleteStrategy() {
      const messageKey = String(this.robotState) === '2' ? 'robot.delete_sj_for_closed_bot' : 'robot.deleting_strategy'
      this.$alert(`<span style="display:block;margin-top:5px;color:#606266;line-height:20px">${this.$t(messageKey)}</span>`, '', {
        dangerouslyUseHTMLString: true,
        showCancelButton: true
      }).then(async () => {
        const res = await deleteNewStrategy(this.robotType, this.items.sid)
        if (!res) return
        if (res.success) {
          this.$message.success(this.$t('robot.del_success'))
          this.$router.back()
        } else {
          this.$message.error(res.message)
        }
      }).catch(() => {})
    },
    extractList(payload) {
      if (Array.isArray(payload)) return payload
      if (!payload || typeof payload !== 'object') return []
      const keys = ['list', 'records', 'rows', 'content', 'positionList', 'orderList', 'data']
      for (let i = 0; i < keys.length; i++) {
        if (Array.isArray(payload[keys[i]])) return payload[keys[i]]
      }
      if (payload.symbol || payload.instrument_id || payload.instId) return [payload]
      return []
    },
    extractTotal(payload, fallback) {
      if (!payload || Array.isArray(payload) || typeof payload !== 'object') return fallback
      return Number(this.rowValue(payload, ['totalRow', 'total', 'totalElements', 'count'], fallback)) || 0
    },
    normalizeProfitPoints(payload) {
      if (payload && !Array.isArray(payload) && typeof payload === 'object') {
        const dates = this.rowValue(payload, ['dates', 'dateList', 'timeList', 'xAxis'], [])
        const values = this.rowValue(payload, ['profits', 'profitList', 'valueList', 'yAxis'], [])
        if (Array.isArray(dates) && Array.isArray(values)) {
          return dates.map((date, index) => ({ date, value: Number(values[index]) || 0 }))
        }
      }
      return this.extractList(payload).map((item, index) => ({
        date: this.rowValue(item, ['date', 'day', 'time', 'create_time', 'add_time'], index + 1),
        value: Number(this.rowValue(item, ['profit', 'profit_total', 'value', 'income', 'earn'], 0)) || 0
      }))
    },
    renderProfitChart() {
      const chartElement = this.$refs.profitChart
      if (!chartElement || !this.profitPoints.length) return
      if (this.profitChart && this.profitChart.getDom() !== chartElement) {
        this.profitChart.dispose()
        this.profitChart = null
      }
      if (!this.profitChart) this.profitChart = this.$echarts.init(chartElement)
      this.profitChart.setOption({
        color: ['#AB54DB'],
        tooltip: {
          trigger: 'axis',
          valueFormatter: value => `${this.formatNumber(value)} ${this.settlementCoin}`
        },
        grid: { top: 24, right: 24, bottom: 30, left: 54 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.profitPoints.map(item => item.date),
          axisLine: { lineStyle: { color: '#D9DEE8' } },
          axisLabel: { color: '#72777E' }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: '#EEF1F6' } },
          axisLabel: { color: '#72777E' }
        },
        series: [{
          name: this.$t('robot.Total_revenue'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: this.profitPoints.map(item => item.value),
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(171,84,219,0.28)' },
                { offset: 1, color: 'rgba(171,84,219,0.02)' }
              ]
            }
          }
        }]
      }, true)
    },
    resizeChart() {
      if (this.profitChart) this.profitChart.resize()
    },
    rowValue(row, keys, fallback) {
      const source = row || {}
      for (let i = 0; i < keys.length; i++) {
        const value = source[keys[i]]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return fallback
    },
    formatNumber(value, digits) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '0.00'
      const precision = digits === undefined ? 2 : digits
      return number.toFixed(precision)
    },
    numberClass(value) {
      const number = Number(value)
      if (number > 0) return 'positive'
      if (number < 0) return 'negative'
      return ''
    },
    tabLabel(key, total) {
      return total ? `${this.$t(key)} (${total})` : this.$t(key)
    },
    positionSideClass(row) {
      const side = String(this.rowValue(row, ['side', 'direction', 'posSide'], '')).toLowerCase()
      return side === 'buy' || side === 'long' || side === '1' ? 'positive' : 'negative'
    },
    positionSideText(row) {
      return this.positionSideClass(row) === 'positive' ? this.$t('robot.long') : this.$t('robot.short')
    },
    orderSideClass(row) {
      const side = String(this.rowValue(row, ['side', 'direction'], '')).toLowerCase()
      return side === 'buy' || side === 'long' || side === '1' ? 'positive' : 'negative'
    },
    orderSideText(row) {
      const side = String(this.rowValue(row, ['side', 'direction'], '')).toLowerCase()
      const type = String(this.rowValue(row, ['type', 'order_type'], '')).toLowerCase()
      if (type === 'open' && side === 'buy') return this.$t('robot.buy_to_open_long')
      if (type === 'open' && side === 'sell') return this.$t('robot.sell_short')
      if (type === 'close' && side === 'buy') return this.$t('robot.buy_short')
      if (type === 'close' && side === 'sell') return this.$t('robot.sell_long')
      return side === 'buy' || side === 'long' || side === '1' ? this.$t('robot.buy') : this.$t('robot.sell')
    },
    orderStateText(row) {
      const state = String(this.rowValue(row, ['state', 'status'], '0'))
      if (state === '-1' || state === 'cancelled' || state === 'canceled') return this.$t('robot.cancelled')
      if (state === '0' || state === 'pending' || state === 'open') return this.$t('robot.pending_order')
      return this.$t('robot.completed')
    }
  }
}
</script>

<style scoped>
.astra-detail {
  min-height: calc(100vh - 120px);
  color: #1e2329;
}

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
}

.strategy-number {
  display: inline-block;
  margin-left: 10px;
}

.header-card {
  margin-top: 12px;
}

.header-card,
.summary-card,
.info-card,
.chart-card,
.records-card {
  border-color: #e8ebf2;
}

.summary-card,
.info-card,
.chart-card,
.records-card {
  margin-top: 12px;
}

.strategy-header,
.strategy-identity,
.strategy-name-row,
.strategy-actions {
  display: flex;
  align-items: center;
}
.strategy-name-row span:nth-child(2) {
  font-size: 14px;
  font-family: PingFangTC-Regular, PingFangTC;
  font-weight: 400;
  display: inline-block;
  color: #E3B936;
  vertical-align: middle;
  background: #FEFBF3;
  padding: 6px 12px;
  border-radius: 2px;
  margin-left: 9px;
}

.strategy-header {
  justify-content: space-between;
  min-height: 52px;
  gap: 20px;
}

.strategy-identity {
  min-width: 0;
}

.strategy-icon {
  width: 42px;
  height: 42px;
  object-fit: contain;
  margin-right: 14px;
}

.strategy-name {
  font-size: 18px;
  font-weight: 600;
}

.running-icon {
  width: 18px;
  height: 18px;
  margin-left: 12px;
}

.strategy-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-top: 7px;
  color: #72777e;
  font-size: 14px;
}

.strategy-actions {
  flex-shrink: 0;
  gap: 10px;
}

.strategy-actions .el-button + .el-button {
  margin-left: 0;
}

.outline-button {
  color: #ab54db !important;
  border-color: #ab54db;
  background: #fff;
}

.primary-button {
  color: #fff !important;
  border-color: #ab54db;
  background: #ab54db;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
}

.metric-item {
  min-width: 0;
}

.metric-center {
  text-align: center;
}

.metric-right {
  text-align: right;
}

.metric-label,
.info-item span {
  display: block;
  color: #72777e;
  font-size: 14px;
}

.metric-value {
  display: block;
  margin-top: 8px;
  color: #1e2329;
  font-size: 20px;
  font-weight: 600;
}

.secondary-value {
  display: block;
  margin-top: 7px;
  color: #1e2329;
  font-size: 16px;
  font-weight: 500;
}

.section-divider {
  height: 1px;
  margin: 22px 0;
  background: #eaecef;
}

.section-divider.compact {
  margin: 16px 0;
}

.card-heading {
  color: #1e2329;
  font-size: 16px;
  font-weight: 500;
}

.chart-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chart-month {
  color: #72777e;
  font-size: 13px;
  font-weight: 400;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
}

.info-item strong {
  display: block;
  margin-top: 7px;
  font-size: 15px;
  font-weight: 500;
}

.profit-chart {
  width: 100%;
  height: 260px;
}

.profit-month-picker {
  width: 125px !important;
}

.mobile-profit-change {
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #eaecef;
}

.positive {
  color: #2cc197 !important;
}

.negative {
  color: #dc4d4d !important;
}

.side-text {
  margin-left: 6px;
}

.records-pagination {
  margin: 24px 0 4px;
  text-align: right;
}

.records-card >>> .el-tabs__item.is-active {
  color: #ab54db;
}

.records-card >>> .el-tabs__active-bar {
  background-color: #ab54db;
}

.detail_title {
  position: relative;
}

.detail_title span:nth-of-type(1) {
  display: inline-block;
  margin-top: 2px;
  color: #000;
  font-size: 16px;
  font-weight: 600;
  vertical-align: middle;
}

.detail_title span:nth-of-type(2) {
 font-size: 14px;
  font-family: PingFangTC-Regular, PingFangTC;
  font-weight: 400;
  display: inline-block;
  color: #E3B936;
  vertical-align: middle;
  background: #FEFBF3;
  padding: 6px 12px;
  border-radius: 2px;
  margin-left: 9px;
}

.detail_title div {
  display: inline-block;
  position: absolute;
  right: 0;
}

.detail_title_button_w {
  padding: 9px 17px;
  color: #ab54db !important;
  border: 1px solid #ab54db;
  border-radius: 2px;
  background: #fff;
  font-size: 14px;
  font-weight: 400;
}

.total_revenue_content_bottom {
  color: #1e2329;
  font-size: 16px;
  font-weight: 400;
}

.side_color_red {
  color: #ff4d4d !important;
}

.side_color_green {
  color: #52c41a !important;
}

>>> .el-tabs__nav-wrap::after {
  height: 1px;
}

>>> .el-tabs__item.is-active {
  color: #ab54db;
}

>>> .el-tabs__active-bar {
  background-color: #ab54db;
}

@media screen and (max-width: 900px) {
  .astra-detail {
    min-height: 100vh;
  }

  >>> .el-tabs__item {
    padding: 0 9px;
  }

  .profit-chart {
    height: 220px;
  }
}
</style>
